import { logClientAuditEvent } from '../services/auditTrailApi';

const PRODUCTION_MODULE = 'PRODUCTIONS';

export interface ProductionPassAuditSnapshot {
  passId: number;
  passName: string;
  welduidSEventTorch: string | null;
  stationNumber: string | null;
  bugType: string | null;
  torch: string | null;
  startTime: string | null;
  endTime: string | null;
}

export function buildProductionPassAuditSnapshot(pass: {
  id: number;
  name?: string;
  welduid_s_event_torch?: string;
  stationNumber?: string;
  bugType?: string;
  torch?: string;
  startTime?: string;
  endTime?: string;
}): ProductionPassAuditSnapshot {
  return {
    passId: pass.id,
    passName: pass.name ?? '',
    welduidSEventTorch: pass.welduid_s_event_torch ?? null,
    stationNumber: pass.stationNumber ?? null,
    bugType: pass.bugType ?? null,
    torch: pass.torch ?? null,
    startTime: pass.startTime ?? null,
    endTime: pass.endTime ?? null,
  };
}

export function logProductionPassesMerged(params: {
  projectId: number | string;
  projectName?: string | null;
  weldNumber: string;
  jobNumber: string;
  primaryWelduidSEventTorch: string;
  mergedWelduidSEventTorchList: string[];
  passes: ProductionPassAuditSnapshot[];
}): void {
  const passCount = params.passes.length;
  if (passCount < 2) return;

  logClientAuditEvent({
    eventType: 'PRODUCTION_WELD_PASSES_MERGED',
    module: PRODUCTION_MODULE,
    entityType: 'WELD_PASS',
    entityId: params.primaryWelduidSEventTorch || params.weldNumber,
    entityName: params.passes[0]?.passName || params.weldNumber,
    projectId: typeof params.projectId === 'number' ? params.projectId : Number(params.projectId) || undefined,
    projectName: params.projectName ?? undefined,
    weldId: params.weldNumber,
    summary: `Merged ${passCount} passes for weld ${params.weldNumber} into ${params.primaryWelduidSEventTorch}.`,
    displayTitle: params.weldNumber,
    actionDetails: {
      weldNumber: params.weldNumber,
      jobNumber: params.jobNumber,
      primaryWelduidSEventTorch: params.primaryWelduidSEventTorch,
      mergedWelduidSEventTorchList: params.mergedWelduidSEventTorchList,
      passCount,
      before: {
        passes: params.passes,
      },
      after: {
        primaryWelduidSEventTorch: params.primaryWelduidSEventTorch,
        mergedPassCount: passCount,
      },
    },
  });
}

export function logProductionPassesUnmerged(params: {
  projectId: number | string;
  projectName?: string | null;
  weldNumber: string;
  jobNumber: string;
  welduidSEventTorchList: string[];
  passes: ProductionPassAuditSnapshot[];
}): void {
  const passCount = params.passes.length;
  if (passCount === 0) return;

  logClientAuditEvent({
    eventType: 'PRODUCTION_WELD_PASSES_UNMERGED',
    module: PRODUCTION_MODULE,
    entityType: 'WELD_PASS',
    entityId: params.welduidSEventTorchList[0] || params.weldNumber,
    entityName: params.passes[0]?.passName || params.weldNumber,
    projectId: typeof params.projectId === 'number' ? params.projectId : Number(params.projectId) || undefined,
    projectName: params.projectName ?? undefined,
    weldId: params.weldNumber,
    summary: `Unmerged ${passCount} pass(es) for weld ${params.weldNumber}.`,
    displayTitle: params.weldNumber,
    actionDetails: {
      weldNumber: params.weldNumber,
      jobNumber: params.jobNumber,
      welduidSEventTorchList: params.welduidSEventTorchList,
      passCount,
      before: {
        passes: params.passes,
        mergedWelduidSEventTorchList: params.welduidSEventTorchList,
      },
      after: {
        restoredPassCount: passCount,
      },
    },
  });
}

function formatProjectAuditLabel(projectId?: number | string | null, projectName?: string | null): string {
  const name = projectName?.trim();
  if (name) return ` in ${name}`;
  const id = normalizeProjectId(projectId);
  if (id != null) return ` in project ${id}`;
  return '';
}

function normalizeProjectId(projectId: number | string | null | undefined): number | undefined {
  if (projectId == null) return undefined;
  if (typeof projectId === 'number') return Number.isFinite(projectId) ? projectId : undefined;
  const parsed = Number(projectId);
  return Number.isFinite(parsed) ? parsed : undefined;
}

const REPORT_TYPE_LABELS: Record<string, string> = {
  't-logs': 'T-Logs',
  'pass-view': 'Pass View',
  'zone-view': 'Zone View',
  'project-production-sheet': 'Production Sheet',
};

export function logStatusConfigurationUpdated(params: {
  projectId: number | string;
  projectName?: string | null;
  pipeSize?: string | number | null;
  pipeName?: string | null;
  jobNumber: string;
  weldingSystem?: string | null;
  slopeTimeIn: string | number;
  slopeTimeOut: string | number;
  statusCalculationMethod: string;
  calculationLevel: string;
  enabledParameters: Record<string, boolean>;
  passGroupCount: number;
}): void {
  const jobLabel = params.pipeName ? `${params.jobNumber} (${params.pipeName})` : params.jobNumber;

  logClientAuditEvent({
    eventType: 'PRODUCTION_STATUS_CONFIGURATION_UPDATED',
    module: PRODUCTION_MODULE,
    entityType: 'STATUS_CONFIGURATION',
    entityId: String(params.jobNumber),
    entityName: jobLabel,
    projectId: normalizeProjectId(params.projectId),
    projectName: params.projectName ?? undefined,
    summary: `Updated status configuration for job ${jobLabel}.`,
    displayTitle: jobLabel,
    actionDetails: {
      jobNumber: params.jobNumber,
      pipeName: params.pipeName ?? null,
      pipeSize: params.pipeSize ?? null,
      weldingSystem: params.weldingSystem ?? null,
      after: {
        slopeTimeIn: params.slopeTimeIn,
        slopeTimeOut: params.slopeTimeOut,
        statusCalculationMethod: params.statusCalculationMethod,
        calculationLevel: params.calculationLevel,
        enabledParameters: params.enabledParameters,
        passGroupCount: params.passGroupCount,
      },
    },
  });
}

export function logProductionReportGenerated(params: {
  projectId?: number | null;
  projectName?: string | null;
  jobNumber?: string | null;
  reportType: string;
  reportVersion?: string | null;
  weldNumbers: string[];
  emails?: string[];
  downloadUrl?: string | null;
  deliveryMethod: 'download' | 'email';
}): void {
  const reportLabel = REPORT_TYPE_LABELS[params.reportType] ?? params.reportType;
  const weldCount = params.weldNumbers.length;
  const weldScope =
    weldCount > 0 ? `${weldCount} weld${weldCount === 1 ? '' : 's'}` : 'entire project';

  let summary = `Generated ${reportLabel} report for ${weldScope}`;
  if (params.deliveryMethod === 'email') {
    summary += ' (email delivery)';
  } else if (params.downloadUrl) {
    summary += ' (download link)';
  }
  summary += '.';

  logClientAuditEvent({
    eventType: 'PRODUCTION_REPORT_GENERATED',
    module: PRODUCTION_MODULE,
    entityType: 'REPORT',
    entityId: params.reportType,
    entityName: reportLabel,
    projectId: params.projectId ?? undefined,
    projectName: params.projectName ?? undefined,
    summary,
    displayTitle: `${reportLabel} report`,
    actionDetails: {
      reportType: params.reportType,
      reportLabel,
      reportVersion: params.reportVersion ?? null,
      jobNumber: params.jobNumber ?? null,
      weldNumbers: params.weldNumbers,
      weldCount,
      emails: params.emails ?? [],
      deliveryMethod: params.deliveryMethod,
      downloadUrl: params.downloadUrl ?? null,
      pdfUrl: params.downloadUrl ?? null,
    },
  });
}

export function logPqrReportGenerated(params: {
  projectId?: number | null;
  projectName?: string | null;
  pipeSize?: string | null;
  weldNumbers: string[];
  pqrNumber?: string | null;
  tolerance?: string | null;
  pqrLimitsMode?: string | null;
  reportUrl?: string | null;
}): void {
  const weldCount = params.weldNumbers.length;
  const pqrLabel = params.pqrNumber?.trim() || 'PQR report';
  let summary = `Generated PQR report ${pqrLabel} for ${weldCount} weld${weldCount === 1 ? '' : 's'}`;
  if (params.reportUrl) {
    summary += ' with download link';
  }
  summary += '.';

  logClientAuditEvent({
    eventType: 'PRODUCTION_PQR_REPORT_GENERATED',
    module: PRODUCTION_MODULE,
    entityType: 'PQR_REPORT',
    entityId: params.pqrNumber?.trim() || params.weldNumbers[0] || 'pqr',
    entityName: pqrLabel,
    projectId: params.projectId ?? undefined,
    projectName: params.projectName ?? undefined,
    summary,
    displayTitle: pqrLabel,
    actionDetails: {
      pqrNumber: params.pqrNumber ?? null,
      pipeSize: params.pipeSize ?? null,
      weldNumbers: params.weldNumbers,
      weldCount,
      tolerance: params.tolerance ?? null,
      pqrLimitsMode: params.pqrLimitsMode ?? null,
      reportUrl: params.reportUrl ?? null,
      pdfUrl: params.reportUrl ?? null,
    },
  });
}

export function logPumpHouseCoatingUploaded(params: {
  projectId: number | string;
  projectName?: string | null;
  pipeId?: number | string | null;
  pipeName?: string | null;
  fileName: string;
  fileUrl?: string | null;
  s3Key?: string | null;
  rowCount: number;
  coatingMaterial?: string | null;
  coatingType?: string | null;
  baseBatch?: string | null;
  hardenerBatch?: string | null;
}): void {
  let summary = `Uploaded pump house coating file "${params.fileName}" (${params.rowCount} rows)`;
  if (params.fileUrl) {
    summary += ' with file URL';
  }
  summary += '.';

  logClientAuditEvent({
    eventType: 'PRODUCTION_PUMP_HOUSE_COATING_UPLOADED',
    module: 'COATING',
    entityType: 'COATING_UPLOAD',
    entityId: params.fileName,
    entityName: params.fileName,
    projectId: normalizeProjectId(params.projectId),
    projectName: params.projectName ?? undefined,
    summary,
    displayTitle: params.fileName,
    actionDetails: {
      fileName: params.fileName,
      fileUrl: params.fileUrl ?? null,
      s3Link: params.fileUrl ?? null,
      s3Key: params.s3Key ?? null,
      pipeId: params.pipeId ?? null,
      pipeName: params.pipeName ?? null,
      rowCount: params.rowCount,
      coatingMaterial: params.coatingMaterial ?? null,
      coatingType: params.coatingType ?? null,
      baseBatch: params.baseBatch ?? null,
      hardenerBatch: params.hardenerBatch ?? null,
    },
  });
}

export function logWeldLogUploaded(params: {
  projectId: number | string;
  projectName?: string | null;
  fileName: string;
  machineType: string;
  recordCount: number;
  tRecordCount: number;
  sRecordCount: number;
  cRecordCount: number;
  weldNumbers?: string[];
  jobNumbers?: string[];
}): void {
  const weldCount = params.weldNumbers?.length ?? 0;

  logClientAuditEvent({
    eventType: 'PRODUCTION_WELD_LOG_UPLOADED',
    module: PRODUCTION_MODULE,
    entityType: 'WELD_LOG',
    entityId: params.fileName,
    entityName: params.fileName,
    projectId: normalizeProjectId(params.projectId),
    projectName: params.projectName ?? undefined,
    summary: `Uploaded weld log "${params.fileName}" (${params.tRecordCount} pass records${weldCount ? `, ${weldCount} welds` : ''}).`,
    displayTitle: params.fileName,
    actionDetails: {
      fileName: params.fileName,
      machineType: params.machineType,
      recordCount: params.recordCount,
      tRecordCount: params.tRecordCount,
      sRecordCount: params.sRecordCount,
      cRecordCount: params.cRecordCount,
      weldNumbers: params.weldNumbers ?? [],
      jobNumbers: params.jobNumbers ?? [],
      weldCount,
    },
  });
}

export function logWeldNumberUpdated(params: {
  projectId?: number | null;
  projectName?: string | null;
  productionWeldId?: number | string | null;
  beforeWeldNumber: string;
  afterWeldNumber: string;
  beforeJobNumber?: string | null;
  afterJobNumber?: string | null;
}): void {
  const weldChanged = params.beforeWeldNumber !== params.afterWeldNumber;
  const jobChanged =
    params.beforeJobNumber != null &&
    params.afterJobNumber != null &&
    params.beforeJobNumber !== params.afterJobNumber;

  if (!weldChanged && !jobChanged) {
    return;
  }

  const summaryParts: string[] = [];
  if (weldChanged) {
    summaryParts.push(`weld number ${params.beforeWeldNumber} → ${params.afterWeldNumber}`);
  }
  if (jobChanged) {
    summaryParts.push(`job number ${params.beforeJobNumber} → ${params.afterJobNumber}`);
  }

  logClientAuditEvent({
    eventType: 'PRODUCTION_WELD_NUMBER_UPDATED',
    module: PRODUCTION_MODULE,
    entityType: 'WELD',
    entityId: params.afterWeldNumber || params.beforeWeldNumber,
    entityName: params.afterWeldNumber || params.beforeWeldNumber,
    projectId: params.projectId ?? undefined,
    projectName: params.projectName ?? undefined,
    weldId: params.afterWeldNumber || params.beforeWeldNumber,
    summary: `Updated ${summaryParts.join(' and ')}.`,
    displayTitle: params.afterWeldNumber || params.beforeWeldNumber,
    actionDetails: {
      before: {
        weldNumber: params.beforeWeldNumber,
        jobNumber: params.beforeJobNumber ?? null,
      },
      after: {
        weldNumber: params.afterWeldNumber,
        jobNumber: params.afterJobNumber ?? null,
      },
      weldNumberChanged: weldChanged,
      jobNumberChanged: jobChanged,
      productionWeldId: params.productionWeldId ?? null,
    },
  });
}

export interface WeldParameterPassRenameEntry {
  groupNumber?: string | number;
  torch?: string;
  passName?: string;
  newPassName?: string;
  passIndex?: number;
  passNumber?: string | number;
}

export function logProductionModalOpened(params: {
  modal: string;
  projectId?: number | string | null;
  projectName?: string | null;
  source?: string;
}): void {
  const modal = params.modal?.trim();
  if (!modal) return;

  logClientAuditEvent({
    eventType: 'PRODUCTION_MODAL_OPENED',
    module: PRODUCTION_MODULE,
    entityType: 'UI',
    entityId: modal.toLowerCase().replace(/\s+/g, '-'),
    entityName: modal,
    projectId: normalizeProjectId(params.projectId),
    projectName: params.projectName ?? undefined,
    summary: `Opened ${modal} modal${formatProjectAuditLabel(params.projectId, params.projectName)}.`,
    displayTitle: modal,
    actionDetails: {
      modal,
      source: params.source ?? 'production',
      projectId: normalizeProjectId(params.projectId) ?? null,
      projectName: params.projectName?.trim() || null,
    },
  });
}

export function logJobNumberRenamed(params: {
  projectId?: number | string | null;
  projectName?: string | null;
  pipeId?: number | string | null;
  pipeName?: string | null;
  oldJobNumber: string;
  newJobNumber: string;
}): void {
  const oldJobNumber = params.oldJobNumber.trim();
  const newJobNumber = params.newJobNumber.trim();
  if (!oldJobNumber || !newJobNumber || oldJobNumber.toLowerCase() === newJobNumber.toLowerCase()) {
    return;
  }

  logClientAuditEvent({
    eventType: 'PRODUCTION_JOB_NUMBER_RENAMED',
    module: PRODUCTION_MODULE,
    entityType: 'JOB_NUMBER',
    entityId: newJobNumber,
    entityName: newJobNumber,
    projectId: normalizeProjectId(params.projectId),
    projectName: params.projectName ?? undefined,
    summary: `Renamed job number ${oldJobNumber} → ${newJobNumber}.`,
    displayTitle: newJobNumber,
    actionDetails: {
      before: { jobNumber: oldJobNumber },
      after: { jobNumber: newJobNumber },
      pipeId: params.pipeId ?? null,
      pipeName: params.pipeName ?? null,
    },
  });
}

export function logWeldItemsAssignedToProject(params: {
  sourceProjectId: number | string;
  sourceProjectName?: string | null;
  targetProjectId: number | string;
  targetProjectName?: string | null;
  weldNumbers: string[];
  weldAndJobEntries?: string[];
  passViewRecordsMoved?: number;
  weldJobCombinations?: string[];
}): void {
  const weldCount = params.weldNumbers.length;
  if (weldCount === 0) return;

  const sourceLabel =
    params.sourceProjectName?.trim() ||
    (normalizeProjectId(params.sourceProjectId) != null
      ? `project ${params.sourceProjectId}`
      : 'source project');
  const targetLabel =
    params.targetProjectName?.trim() ||
    (normalizeProjectId(params.targetProjectId) != null
      ? `project ${params.targetProjectId}`
      : 'target project');

  logClientAuditEvent({
    eventType: 'PRODUCTION_WELD_ITEMS_ASSIGNED_TO_PROJECT',
    module: PRODUCTION_MODULE,
    entityType: 'WELD',
    entityId: params.weldNumbers[0],
    entityName: params.weldNumbers[0],
    projectId: normalizeProjectId(params.sourceProjectId),
    projectName: params.sourceProjectName ?? undefined,
    weldId: params.weldNumbers[0],
    summary: `Moved ${weldCount} weld${weldCount === 1 ? '' : 's'} from ${sourceLabel} to ${targetLabel}.`,
    displayTitle: params.weldNumbers.slice(0, 3).join(', '),
    actionDetails: {
      weldCount,
      before: {
        projectId: normalizeProjectId(params.sourceProjectId) ?? params.sourceProjectId,
        projectName: params.sourceProjectName?.trim() || null,
        weldNumbers: params.weldNumbers,
        weldAndJobEntries: params.weldAndJobEntries ?? [],
      },
      after: {
        projectId: normalizeProjectId(params.targetProjectId) ?? params.targetProjectId,
        projectName: params.targetProjectName?.trim() || null,
        weldNumbers: params.weldNumbers,
        weldAndJobEntries: params.weldJobCombinations ?? params.weldAndJobEntries ?? [],
        passViewRecordsMoved: params.passViewRecordsMoved ?? null,
      },
    },
  });
}

export function logPipeAssignedToWeld(params: {
  projectId?: number | string | null;
  projectName?: string | null;
  pipeId?: number | string | null;
  pipeName?: string | null;
  weldNumbers: string[];
  jobNumbers?: string[];
}): void {
  const weldCount = params.weldNumbers.length;
  if (weldCount === 0) return;

  const pipeLabel = params.pipeName ?? (params.pipeId != null ? `pipe ${params.pipeId}` : 'pipe');

  logClientAuditEvent({
    eventType: 'PRODUCTION_PIPE_ASSIGNED_TO_WELD',
    module: PRODUCTION_MODULE,
    entityType: 'WELD',
    entityId: params.weldNumbers[0],
    entityName: params.weldNumbers[0],
    projectId: normalizeProjectId(params.projectId),
    projectName: params.projectName ?? undefined,
    summary: `Assigned ${weldCount} weld${weldCount === 1 ? '' : 's'} to ${pipeLabel}.`,
    displayTitle: pipeLabel,
    actionDetails: {
      pipeId: params.pipeId ?? null,
      pipeName: params.pipeName ?? null,
      weldNumbers: params.weldNumbers,
      jobNumbers: params.jobNumbers ?? [],
      weldCount,
    },
  });
}

export function logReportColumnConfigUpdated(params: {
  projectId?: number | string | null;
  projectName?: string | null;
  mode?: 'report' | 'status';
  enabledColumnCount: number;
  enabledColumns: string[];
  units?: Record<string, string>;
}): void {
  const modeLabel = params.mode === 'status' ? 'status configuration' : 'report generation';

  logClientAuditEvent({
    eventType: 'PRODUCTION_REPORT_COLUMNS_UPDATED',
    module: PRODUCTION_MODULE,
    entityType: 'REPORT_COLUMNS',
    entityId: String(params.projectId ?? 'project'),
    entityName: modeLabel,
    projectId: normalizeProjectId(params.projectId),
    projectName: params.projectName ?? undefined,
    summary: `Updated report column settings for ${modeLabel} (${params.enabledColumnCount} columns).`,
    displayTitle: modeLabel,
    actionDetails: {
      mode: params.mode ?? 'report',
      enabledColumnCount: params.enabledColumnCount,
      enabledColumns: params.enabledColumns,
      units: params.units ?? {},
    },
  });
}

export function logIrnReportGenerated(params: {
  projectId?: number | string | null;
  projectName?: string | null;
  irnType: string;
  assemblyNumbers?: string[];
  downloadUrl?: string | null;
  fileName?: string | null;
}): void {
  const typeLabel = params.irnType?.trim() || 'IRN';
  const assemblyCount = params.assemblyNumbers?.length ?? 0;
  let summary = `Generated ${typeLabel} inspection release note`;
  if (assemblyCount > 0) {
    summary += ` for ${assemblyCount} assembly${assemblyCount === 1 ? '' : 'ies'}`;
  }
  if (params.downloadUrl) {
    summary += ' with download link';
  }
  summary += '.';

  logClientAuditEvent({
    eventType: 'PRODUCTION_IRN_REPORT_GENERATED',
    module: PRODUCTION_MODULE,
    entityType: 'IRN_REPORT',
    entityId: typeLabel,
    entityName: `${typeLabel} IRN`,
    projectId: normalizeProjectId(params.projectId),
    projectName: params.projectName ?? undefined,
    summary,
    displayTitle: `${typeLabel} IRN`,
    actionDetails: {
      irnType: params.irnType,
      assemblyNumbers: params.assemblyNumbers ?? [],
      assemblyCount,
      downloadUrl: params.downloadUrl ?? null,
      fileName: params.fileName ?? null,
      pdfUrl: params.downloadUrl ?? null,
    },
  });
}

export function logSpoolNcrRegistered(params: {
  projectId?: number | string | null;
  projectName?: string | null;
  pipeId?: number | string | null;
  spoolId?: number | string | null;
  ncrId?: string | null;
  disposition?: string | null;
  mode?: 'save' | 'notify_buyer';
}): void {
  const ncrLabel = params.ncrId?.trim() || 'NCR';

  logClientAuditEvent({
    eventType: 'SPOOL_NCR_REGISTERED',
    module: 'SPOOL',
    entityType: 'NCR',
    entityId: ncrLabel,
    entityName: ncrLabel,
    projectId: normalizeProjectId(params.projectId),
    projectName: params.projectName ?? undefined,
    summary: `Updated NCR ${ncrLabel}${params.mode === 'notify_buyer' ? ' and notified buyer' : ''}.`,
    displayTitle: ncrLabel,
    actionDetails: {
      pipeId: params.pipeId ?? null,
      spoolId: params.spoolId ?? null,
      disposition: params.disposition ?? null,
      mode: params.mode ?? 'save',
    },
  });
}

export function logWeldParameterPassesRenamed(params: {
  projectId?: number | null;
  projectName?: string | null;
  jobNumber: string | number;
  machineType: 'M500' | 'P625' | 'P600z' | string;
  pipeParamKey?: string | null;
  renamedPasses: WeldParameterPassRenameEntry[];
}): void {
  const passCount = params.renamedPasses.length;
  if (passCount === 0) return;

  const jobNumber = String(params.jobNumber);
  const machineLabel = String(params.machineType).toUpperCase();

  logClientAuditEvent({
    eventType: 'PRODUCTION_WELD_PARAMETER_PASS_RENAMED',
    module: PRODUCTION_MODULE,
    entityType: 'WELD_PARAMETER_JOB',
    entityId: jobNumber,
    entityName: `${machineLabel} job ${jobNumber}`,
    projectId: normalizeProjectId(params.projectId),
    projectName: params.projectName ?? undefined,
    summary: `Renamed ${passCount} weld parameter pass(es) for ${machineLabel} job ${jobNumber}.`,
    displayTitle: jobNumber,
    actionDetails: {
      operation: 'RENAME_PASS',
      jobNumber,
      machineType: params.machineType,
      pipeParamKey: params.pipeParamKey ?? null,
      passCount,
      renamedPasses: params.renamedPasses.map((entry) => ({
        groupNumber: entry.groupNumber ?? null,
        torch: entry.torch ?? null,
        passName: entry.passName ?? null,
        newPassName: entry.newPassName ?? null,
      })),
    },
  });
}
